import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { SidebarService } from './sidebar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../../../core/toastr/toaster.service';
import { ModalSignOutService } from '../../../../core/modal-sign-out/modal-sign-out.service';
import { User } from '../../../../core/models/user';

interface MenuItem {
  routeLink: string;
  label: string;
  icon: string;
  active: boolean;
  businessLineKey?: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})

export class SidebarComponent implements OnInit, OnDestroy {
  isVisible$: Observable<boolean>;
  private sidebarSubscription: Subscription | undefined;
  isMobileOrTablet: boolean = false;

  allMenuItems: MenuItem[] = [
    {
      label: 'Overview',
      icon: 'ri-line-chart-line',
      routeLink: '/dashboard',
      active: true
    },
    {
      label: 'Hotel',
      icon: 'ri-hotel-line',
      routeLink: '/hotel',
      active: false,
      businessLineKey: 'Hotel'
    },
    {
      label: 'Office Lease',
      icon: 'ri-building-2-line',
      routeLink: '/office-lease',
      active: false,
      businessLineKey: 'Office Lease'
    },
    {
      label: 'Golf',
      icon: 'ri-golf-ball-line',
      routeLink: '/golf',
      active: false,
      businessLineKey: 'Golf'
    },
    {
      label: 'Residences',
      icon: 'ri-landscape-line',
      routeLink: '/residences',
      active: false,
      businessLineKey: 'Residence'
    },
    {
      label: 'Sales Activities',
      icon: 'ri-news-line',
      routeLink: '/sales-activities',
      active: false
    }
  ];

  // Menu items yang akan ditampilkan berdasarkan permission
  menuItems: MenuItem[] = [];

  itemCurrentUser: User = {} as User;
  itemBusinessLine: string[] = [];
  itemPicture: string = "";



  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToasterService,
    private modalSignOutService: ModalSignOutService

  ) {
    this.isVisible$ = this.sidebarService.sidebarState$;
    this.checkScreenSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobileOrTablet = window.innerWidth <= 1024;
  }

  ngOnInit(): void {
    const userData = localStorage.getItem('currentUser');
    this.itemCurrentUser = userData ? JSON.parse(userData) : {} as User;
    this.itemBusinessLine = this.itemCurrentUser.business_lines || [];
    this.itemPicture = (this.itemCurrentUser.picture && this.itemCurrentUser.picture.trim() !== '')
      ? this.itemCurrentUser.picture
      : 'assets/images/default-user-profile.png';

    // Filter menu berdasarkan business line
    this.filterMenuItems();

    // Validasi akses dan redirect jika perlu
    this.validateAccess();


    this.sidebarSubscription = this.isVisible$.subscribe(state => {
      // console.log('Sidebar state:', state);
    });

    this.checkScreenSize();
  }

  private filterMenuItems(): void {
    // Reset menu items
    this.menuItems = [];

    // Selalu tampilkan Overview jika user memiliki minimal 1 business line
    if (this.itemBusinessLine.length > 0) {
      this.menuItems.push(this.allMenuItems[0]); // Overview
    }

    // Filter menu berdasarkan business line yang dimiliki user
    const businessLineMenus = this.allMenuItems.filter(item =>
      item.businessLineKey && this.itemBusinessLine.includes(item.businessLineKey)
    );

    this.menuItems.push(...businessLineMenus);

    // Tambahkan Sales Activities jika user memiliki minimal 1 business line
    if (this.itemBusinessLine.length > 0) {
      const salesActivitiesMenu = this.allMenuItems.find(item => item.label === 'Sales Activities');
      if (salesActivitiesMenu) {
        this.menuItems.push(salesActivitiesMenu);
      }
    }
  }

  private validateAccess(): void {
    // Jika tidak memiliki business line sama sekali, redirect ke 404
    if (!this.itemBusinessLine || this.itemBusinessLine.length === 0) {
      this.redirectTo404();
      return;
    }

    // Validasi business line yang valid
    const validBusinessLines = ['Hotel', 'Office Lease', 'Golf', 'Residence'];
    const hasValidBusinessLine = this.itemBusinessLine.some(line =>
      validBusinessLines.includes(line)
    );

    if (!hasValidBusinessLine) {
      this.redirectTo404();
      return;
    }

    // Validasi akses ke route saat ini
    this.validateCurrentRoute();
  }

  private validateCurrentRoute(): void {
    const currentUrl = this.router.url;

    // Jika sedang di route selain dashboard, login, atau error pages
    if (!currentUrl.includes('/dashboard') &&
      !currentUrl.includes('/login') &&
      !currentUrl.includes('/404') &&
      !currentUrl.includes('/500') &&
      !currentUrl.includes('/userprofile')) {

      const hasAccessToCurrentRoute = this.hasAccessToRoute(currentUrl);

      if (!hasAccessToCurrentRoute) {
        // Redirect ke dashboard jika tidak memiliki akses ke route saat ini
        this.router.navigate(['/dashboard'], { replaceUrl: true });
      }
    }
  }

  private hasAccessToRoute(url: string): boolean {
    // Cek apakah user memiliki akses ke route tertentu
    if (url.includes('/hotel')) {
      return this.itemBusinessLine.includes('Hotel');
    }
    if (url.includes('/office-lease')) {
      return this.itemBusinessLine.includes('Office Lease');
    }
    if (url.includes('/golf')) {
      return this.itemBusinessLine.includes('Golf');
    }
    if (url.includes('/residences')) {
      return this.itemBusinessLine.includes('Residence');
    }
    if (url.includes('/sales-activities')) {
      return this.itemBusinessLine.length > 0; // Sales activities tersedia jika ada minimal 1 business line
    }

    return true; // Route lainnya seperti dashboard, userprofile selalu bisa diakses
  }

  private redirectTo404(): void {
    this.router.navigate(['/404'], {
      replaceUrl: true,
      skipLocationChange: false
    });

    // Disable browser back button dengan menambah history entry
    history.pushState(null, '', window.location.href);
    window.onpopstate = () => {
      history.pushState(null, '', window.location.href);
    };
  }

  ngOnDestroy(): void {
    if (this.sidebarSubscription) {
      this.sidebarSubscription.unsubscribe();
    }
  }

  closeSidebar(): void {
    this.sidebarService.closeSidebar();
  }

  onMenuClick(selectedItem: MenuItem): void {

    // **TAMBAHAN BARU: Validasi permission sebelum navigasi**
    // if (selectedItem.businessLineKey && !this.itemBusinessLine.includes(selectedItem.businessLineKey)) {
    //   this.toastr.error('You do not have permission to access this module', 'Access Denied');
    //   return; // Stop execution jika tidak memiliki permission
    // }

    // **TAMBAHAN BARU: Validasi jika user tidak memiliki business line sama sekali**
    // if (this.itemBusinessLine.length === 0) {
    //   this.toastr.error('Your account does not have access to any business modules', 'Access Restricted');
    //   this.router.navigate(['/404'], { replaceUrl: true });
    //   return;
    // }

    // Set active menu
    this.setActiveMenu(selectedItem);

    // **TAMBAHAN BARU: Navigate ke route yang dipilih**
    this.router.navigate([selectedItem.routeLink]);

    // Set active menu
    this.setActiveMenu(selectedItem);

    // Close sidebar on mobile devices
    if (window.innerWidth <= 768) {
      return this.closeSidebar();
    }


    // Optional: Tambahkan delay kecil sebelum menutup sidebar
    setTimeout(() => {
      this.closeSidebar();
    }, 150);
  }

  setActiveMenu(selectedItem: any) {
    this.menuItems.forEach(item => item.active = item === selectedItem);
  }

  onSingleClick() {
    this.closeSidebar();
  }

  openSignOutModal(): void {
    this.modalSignOutService.openModal();

  }

}
